import { useState, useEffect, useCallback } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { db, auth } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;1,400&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #fff; font-family: 'DM Sans', sans-serif; -webkit-font-smoothing: antialiased; overflow-x: hidden; }
  input, select { font-family: inherit; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Login ──────────────────────────────────────────────────────────────────────

const LoginPage = styled.div`
  min-height: 100vh;
  background: #040008;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;
const LoginCard = styled.div`
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(168, 85, 247, 0.18);
  border-radius: 20px;
  padding: 2.5rem;
  animation: ${fadeIn} 0.5s ease-out;
  &::before {
    content: "";
    display: block;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(168, 85, 247, 0.5),
      transparent
    );
    margin-bottom: 2rem;
  }
`;
const LoginTitle = styled.h1`
  font-family: "Playfair Display", serif;
  font-size: 1.8rem;
  font-weight: 400;
  font-style: italic;
  color: #fff;
  margin-bottom: 0.4rem;
`;
const LoginSub = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 0.78rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 2rem;
`;
const loginField = `
  width: 100%;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-size: 0.9rem; font-weight: 300;
  color: rgba(255,255,255,0.85);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  margin-bottom: 0.9rem; display: block;
  &::placeholder { color: rgba(255,255,255,0.18); }
  &:focus {
    border-color: rgba(168,85,247,0.5);
    box-shadow: 0 0 0 3px rgba(168,85,247,0.08);
  }
`;
const LoginInput = styled.input`
  ${loginField}
`;
const LoginBtn = styled.button`
  width: 100%;
  height: 46px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #a855f7, #c026d3);
  box-shadow: 0 6px 24px rgba(124, 58, 237, 0.4);
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  margin-top: 0.5rem;
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(124, 58, 237, 0.5);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const LoginError = styled.div`
  font-size: 0.75rem;
  color: #f87171;
  text-align: center;
  margin-top: 0.75rem;
`;
const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
`;

// ── Admin shell ────────────────────────────────────────────────────────────────

const AdminLayout = styled.div`
  min-height: 100vh;
  background: #f8f6ff;
`;
const AdminNav = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  border-bottom: 1px solid rgba(168, 85, 247, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 clamp(1.5rem, 4vw, 3rem);
  height: 62px;
`;
const NavBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const NavLogo = styled.div`
  font-family: "Playfair Display", serif;
  font-size: 1.3rem;
  font-weight: 700;
  font-style: italic;
  background: linear-gradient(135deg, #7c3aed, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;
const NavBadge = styled.div`
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #a855f7;
  background: rgba(168, 85, 247, 0.1);
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 100px;
  padding: 2px 8px;
`;
const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;
const NavEmail = styled.div`
  font-size: 0.75rem;
  color: rgba(10, 0, 16, 0.35);
  @media (max-width: 600px) {
    display: none;
  }
`;
const SignOutBtn = styled.button`
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(10, 0, 16, 0.45);
  background: none;
  border: 1px solid rgba(10, 0, 16, 0.1);
  border-radius: 8px;
  padding: 6px 14px;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
  &:hover {
    background: rgba(168, 85, 247, 0.05);
    color: #7c3aed;
    border-color: rgba(168, 85, 247, 0.2);
  }
`;

// ── Tab bar ────────────────────────────────────────────────────────────────────

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(168, 85, 247, 0.1);
  background: #fff;
  padding: 0 clamp(1.5rem, 4vw, 3rem);
  gap: 0;
`;
const TabBtn = styled.button`
  position: relative;
  padding: 0 1.5rem;
  height: 48px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: ${({ $active }) => ($active ? "500" : "400")};
  color: ${({ $active }) => ($active ? "#7c3aed" : "rgba(10,0,16,0.4)")};
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: #7c3aed;
    opacity: ${({ $active }) => ($active ? "1" : "0")};
    transition: opacity 0.2s;
    border-radius: 2px 2px 0 0;
  }
  &:hover {
    color: #7c3aed;
  }
`;
const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 100px;
  background: ${({ $active }) =>
    $active ? "rgba(124,58,237,0.12)" : "rgba(10,0,16,0.06)"};
  color: ${({ $active }) => ($active ? "#7c3aed" : "rgba(10,0,16,0.35)")};
  font-size: 0.65rem;
  font-weight: 600;
`;

// ── Dashboard ──────────────────────────────────────────────────────────────────

const Dashboard = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: clamp(2rem, 4vw, 3rem) clamp(1.5rem, 4vw, 3rem);
  animation: ${fadeIn} 0.4s ease-out;
`;

// Stats
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;
const StatCard = styled.div`
  background: #fff;
  border: 1px solid rgba(168, 85, 247, 0.1);
  border-radius: 14px;
  padding: 1.2rem 1.4rem;
`;
const StatNum = styled.div`
  font-family: "Playfair Display", serif;
  font-size: 2rem;
  font-weight: 700;
  color: #0a0010;
  line-height: 1;
  margin-bottom: 0.3rem;
  span {
    color: #a855f7;
    font-size: 1.2rem;
  }
`;
const StatLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(10, 0, 16, 0.35);
`;

// Toolbar
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.2rem;
  flex-wrap: wrap;
`;
const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(10, 0, 16, 0.1);
  background: #fff;
  padding: 0 0.9rem;
  font-size: 0.85rem;
  font-weight: 300;
  color: #0a0010;
  outline: none;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  &::placeholder {
    color: rgba(10, 0, 16, 0.28);
  }
  &:focus {
    border-color: rgba(168, 85, 247, 0.4);
    box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.06);
  }
`;
const FilterSelect = styled.select`
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(10, 0, 16, 0.1);
  background: #fff;
  padding: 0 0.9rem;
  font-size: 0.82rem;
  color: rgba(10, 0, 16, 0.65);
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;
  &:focus {
    border-color: rgba(168, 85, 247, 0.4);
  }
`;
const ToggleGroup = styled.div`
  display: flex;
  border: 1px solid rgba(10, 0, 16, 0.1);
  border-radius: 10px;
  overflow: hidden;
`;
const ToggleBtn = styled.button`
  height: 38px;
  padding: 0 14px;
  background: ${({ $active }) => ($active ? "rgba(168,85,247,0.1)" : "#fff")};
  border: none;
  color: ${({ $active }) => ($active ? "#7c3aed" : "rgba(10,0,16,0.4)")};
  font-size: 0.8rem;
  font-weight: ${({ $active }) => ($active ? "500" : "400")};
  cursor: pointer;
  transition:
    background 0.2s,
    color 0.2s;
  &:first-child {
    border-right: 1px solid rgba(10, 0, 16, 0.08);
  }
  &:hover {
    background: rgba(168, 85, 247, 0.06);
    color: #7c3aed;
  }
`;
const ExportBtn = styled.button`
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid rgba(168, 85, 247, 0.25);
  background: rgba(168, 85, 247, 0.06);
  color: #7c3aed;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.2s,
    border-color 0.2s;
  white-space: nowrap;
  &:hover {
    background: rgba(168, 85, 247, 0.12);
    border-color: rgba(168, 85, 247, 0.4);
  }
`;
const ResultCount = styled.div`
  font-size: 0.72rem;
  color: rgba(10, 0, 16, 0.35);
  white-space: nowrap;
`;

// Table
const TableWrap = styled.div`
  background: #fff;
  border: 1px solid rgba(168, 85, 247, 0.1);
  border-radius: 16px;
  overflow: hidden;
  overflow-x: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
`;
const Th = styled.th`
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(10, 0, 16, 0.35);
  padding: 0.9rem 1.2rem;
  text-align: left;
  background: #faf7ff;
  border-bottom: 1px solid rgba(168, 85, 247, 0.08);
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  &:hover {
    color: #7c3aed;
  }
`;
const Tr = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid rgba(10, 0, 16, 0.05);
  }
  &:hover {
    background: #faf7ff;
  }
  animation: ${fadeIn} 0.3s ease-out;
`;
const Td = styled.td`
  font-size: 0.85rem;
  font-weight: 300;
  color: ${({ $dim }) => ($dim ? "rgba(10,0,16,0.4)" : "#0a0010")};
  padding: 0.85rem 1.2rem;
  white-space: nowrap;
`;
const PosBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(168, 85, 247, 0.08);
  border: 1px solid rgba(168, 85, 247, 0.15);
  font-size: 0.72rem;
  font-weight: 600;
  color: #7c3aed;
`;
const StatePill = styled.div`
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 100px;
  background: rgba(168, 85, 247, 0.08);
  font-size: 0.72rem;
  font-weight: 500;
  color: #7c3aed;
`;
const DeleteBtn = styled.button`
  background: none;
  border: none;
  color: rgba(10, 0, 16, 0.2);
  cursor: pointer;
  font-size: 0.9rem;
  padding: 4px;
  border-radius: 6px;
  transition:
    color 0.15s,
    background 0.15s;
  &:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.06);
  }
`;

// Cards
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
`;
const UserCard = styled.div`
  background: #fff;
  border: 1px solid rgba(168, 85, 247, 0.1);
  border-radius: 14px;
  padding: 1.2rem 1.4rem;
  animation: ${fadeIn} 0.3s ease-out;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  &:hover {
    border-color: rgba(168, 85, 247, 0.25);
    box-shadow: 0 4px 20px rgba(168, 85, 247, 0.07);
  }
`;
const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.9rem;
`;
const CardAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #7c3aed, #a855f7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: #fff;
`;
const CardPos = styled.div`
  font-family: "Playfair Display", serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: rgba(168, 85, 247, 0.4);
`;
const CardName = styled.div`
  font-size: 0.92rem;
  font-weight: 500;
  color: #0a0010;
  margin-bottom: 0.2rem;
`;
const CardMeta = styled.div`
  font-size: 0.75rem;
  font-weight: 300;
  color: rgba(10, 0, 16, 0.45);
  margin-bottom: 0.15rem;
`;
const CardState = styled.div`
  margin-top: 0.75rem;
`;

// Preorder-specific
const SizeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(
    135deg,
    rgba(124, 58, 237, 0.12),
    rgba(168, 85, 247, 0.08)
  );
  border: 1px solid rgba(168, 85, 247, 0.25);
  font-size: 0.72rem;
  font-weight: 600;
  color: #7c3aed;
`;
const StatusBadge = styled.div`
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 0.68rem;
  font-weight: 500;
  background: ${({ $status }) => {
    if ($status === "paid") return "rgba(34,197,94,0.1)";
    if ($status === "shipped") return "rgba(59,130,246,0.1)";
    if ($status === "cancelled") return "rgba(239,68,68,0.1)";
    return "rgba(234,179,8,0.1)";
  }};
  color: ${({ $status }) => {
    if ($status === "paid") return "#16a34a";
    if ($status === "shipped") return "#2563eb";
    if ($status === "cancelled") return "#dc2626";
    return "#b45309";
  }};
  border: 1px solid
    ${({ $status }) => {
      if ($status === "paid") return "rgba(34,197,94,0.2)";
      if ($status === "shipped") return "rgba(59,130,246,0.2)";
      if ($status === "cancelled") return "rgba(239,68,68,0.2)";
      return "rgba(234,179,8,0.2)";
    }};
`;
const StatusSelect = styled.select`
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(10, 0, 16, 0.1);
  background: #fff;
  padding: 0 0.5rem;
  font-size: 0.75rem;
  color: rgba(10, 0, 16, 0.65);
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: rgba(168, 85, 247, 0.4);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: rgba(10, 0, 16, 0.3);
  font-size: 0.9rem;
`;

// ── Helpers ────────────────────────────────────────────────────────────────────

const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const PREORDER_STATUSES = ["pending_payment", "paid", "shipped", "cancelled"];

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}
function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Login ──────────────────────────────────────────────────────────────────────

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch {
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginPage>
      <LoginCard>
        <LoginTitle>Admin Access</LoginTitle>
        <LoginSub>NudR dashboard</LoginSub>
        <form onSubmit={handleSubmit}>
          <LoginInput
            type="email"
            placeholder="Admin email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <LoginInput
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPass(e.target.value)}
            required
          />
          {error && <LoginError>{error}</LoginError>}
          <LoginBtn type="submit" disabled={loading}>
            {loading && <Spinner />}
            {loading ? "Signing in..." : "Sign in →"}
          </LoginBtn>
        </form>
      </LoginCard>
    </LoginPage>
  );
}

// ── Waitlist tab (unchanged logic, same as before) ─────────────────────────────

function WaitlistTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "waitlist"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = entries
    .filter((e) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        e.name?.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.phone?.includes(q) ||
        e.state?.toLowerCase().includes(q);
      const matchState = !stateFilter || e.state === stateFilter;
      return matchSearch && matchState;
    })
    .sort((a, b) => {
      let av = a[sortKey],
        bv = b[sortKey];
      if (sortKey === "createdAt") {
        av = a.createdAt?.toMillis?.() || 0;
        bv = b.createdAt?.toMillis?.() || 0;
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Remove this entry from the waitlist?")) return;
    await deleteDoc(doc(db, "waitlist", id));
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };
  const exportCSV = () => {
    const headers = [
      "Position",
      "Name",
      "Email",
      "Phone",
      "State",
      "Date Joined",
    ];
    const rows = filtered.map((e) => [
      e.position,
      `"${e.name}"`,
      e.email,
      e.phone,
      e.state,
      formatDate(e.createdAt),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement("a"), {
      href: url,
      download: "nudr-waitlist.csv",
    }).click();
    URL.revokeObjectURL(url);
  };

  const stateBreakdown = entries.reduce((acc, e) => {
    acc[e.state] = (acc[e.state] || 0) + 1;
    return acc;
  }, {});
  const topState = Object.entries(stateBreakdown).sort(
    (a, b) => b[1] - a[1],
  )[0];
  const todayCount = entries.filter((e) => {
    const d = e.createdAt?.toDate?.();
    return d && d.toDateString() === new Date().toDateString();
  }).length;
  const sortArrow = (key) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  return (
    <>
      <StatsGrid>
        <StatCard>
          <StatNum>{entries.length.toLocaleString()}</StatNum>
          <StatLabel>Total signups</StatLabel>
        </StatCard>
        <StatCard>
          <StatNum>
            {todayCount}
            <span>+</span>
          </StatNum>
          <StatLabel>Joined today</StatLabel>
        </StatCard>
        <StatCard>
          <StatNum>{Object.keys(stateBreakdown).length}</StatNum>
          <StatLabel>States represented</StatLabel>
        </StatCard>
        <StatCard>
          <StatNum>{topState ? topState[0].split(" ")[0] : "—"}</StatNum>
          <StatLabel>Top state · {topState ? topState[1] : 0} people</StatLabel>
        </StatCard>
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Search name, email, phone, state..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FilterSelect
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
        >
          <option value="">All states</option>
          {US_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </FilterSelect>
        <ToggleGroup>
          <ToggleBtn
            $active={view === "table"}
            onClick={() => setView("table")}
          >
            ≡ Table
          </ToggleBtn>
          <ToggleBtn
            $active={view === "cards"}
            onClick={() => setView("cards")}
          >
            ⊞ Cards
          </ToggleBtn>
        </ToggleGroup>
        <ExportBtn onClick={exportCSV}>↓ Export CSV</ExportBtn>
        <ResultCount>
          {filtered.length} result{filtered.length !== 1 ? "s" : ""}
        </ResultCount>
      </Toolbar>

      {loading ? (
        <EmptyState>Loading waitlist…</EmptyState>
      ) : filtered.length === 0 ? (
        <EmptyState>No entries found.</EmptyState>
      ) : view === "table" ? (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th onClick={() => handleSort("position")}>
                  #Pos{sortArrow("position")}
                </Th>
                <Th onClick={() => handleSort("name")}>
                  Name{sortArrow("name")}
                </Th>
                <Th onClick={() => handleSort("email")}>
                  Email{sortArrow("email")}
                </Th>
                <Th>Phone</Th>
                <Th onClick={() => handleSort("state")}>
                  State{sortArrow("state")}
                </Th>
                <Th onClick={() => handleSort("createdAt")}>
                  Joined{sortArrow("createdAt")}
                </Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <Tr key={e.id}>
                  <Td>
                    <PosBadge>#{e.position}</PosBadge>
                  </Td>
                  <Td>{e.name}</Td>
                  <Td $dim>{e.email}</Td>
                  <Td $dim>{e.phone}</Td>
                  <Td>
                    <StatePill>{e.state}</StatePill>
                  </Td>
                  <Td $dim>{formatDate(e.createdAt)}</Td>
                  <Td>
                    <DeleteBtn
                      onClick={() => handleDelete(e.id)}
                      title="Remove"
                    >
                      ✕
                    </DeleteBtn>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      ) : (
        <CardsGrid>
          {filtered.map((e) => (
            <UserCard key={e.id}>
              <CardTop>
                <CardAvatar>{initials(e.name)}</CardAvatar>
                <CardPos>#{e.position}</CardPos>
              </CardTop>
              <CardName>{e.name}</CardName>
              <CardMeta>{e.email}</CardMeta>
              <CardMeta>{e.phone}</CardMeta>
              <CardState>
                <StatePill>{e.state}</StatePill>
              </CardState>
              <div
                style={{
                  marginTop: "0.75rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <CardMeta>{formatDate(e.createdAt)}</CardMeta>
                <DeleteBtn onClick={() => handleDelete(e.id)} title="Remove">
                  ✕
                </DeleteBtn>
              </div>
            </UserCard>
          ))}
        </CardsGrid>
      )}
    </>
  );
}

// ── Preorders tab ──────────────────────────────────────────────────────────────

function PreordersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [sizeFilter, setSizeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "preorders"),
        orderBy("createdAt", "desc"),
      );
      const snap = await getDocs(q);
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (id, newStatus) => {
    await updateDoc(doc(db, "preorders", id), { status: newStatus });
    setOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, status: newStatus } : o)),
    );
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this preorder?")) return;
    await deleteDoc(doc(db, "preorders", id));
    setOrders((prev) => prev.filter((o) => o.id !== id));
  };

  const filtered = orders
    .filter((o) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        o.name?.toLowerCase().includes(q) ||
        o.email?.toLowerCase().includes(q) ||
        o.phone?.includes(q);
      const matchSize = !sizeFilter || o.size === sizeFilter;
      const matchStatus = !statusFilter || o.status === statusFilter;
      return matchSearch && matchSize && matchStatus;
    })
    .sort((a, b) => {
      let av = a[sortKey],
        bv = b[sortKey];
      if (sortKey === "createdAt") {
        av = a.createdAt?.toMillis?.() || 0;
        bv = b.createdAt?.toMillis?.() || 0;
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const exportCSV = () => {
    const headers = ["#", "Name", "Email", "Phone", "Size", "Status", "Date"];
    const rows = filtered.map((o, i) => [
      i + 1,
      `"${o.name}"`,
      o.email,
      o.phone || "",
      o.size,
      o.status,
      formatDate(o.createdAt),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement("a"), {
      href: url,
      download: "nudr-preorders.csv",
    }).click();
    URL.revokeObjectURL(url);
  };

  // Stats
  const paidCount = orders.filter((o) => o.status === "paid").length;
  const shippedCount = orders.filter((o) => o.status === "shipped").length;
  const todayCount = orders.filter((o) => {
    const d = o.createdAt?.toDate?.();
    return d && d.toDateString() === new Date().toDateString();
  }).length;
  const sizeBreakdown = orders.reduce((acc, o) => {
    acc[o.size] = (acc[o.size] || 0) + 1;
    return acc;
  }, {});
  const topSize = Object.entries(sizeBreakdown).sort((a, b) => b[1] - a[1])[0];
  const sortArrow = (key) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

  return (
    <>
      <StatsGrid>
        <StatCard>
          <StatNum>{orders.length}</StatNum>
          <StatLabel>Total pre-orders</StatLabel>
        </StatCard>
        <StatCard>
          <StatNum>
            {paidCount}
            <span> paid</span>
          </StatNum>
          <StatLabel>Payment confirmed</StatLabel>
        </StatCard>
        <StatCard>
          <StatNum>{shippedCount}</StatNum>
          <StatLabel>Shipped</StatLabel>
        </StatCard>
        <StatCard>
          <StatNum>{topSize ? topSize[0] : "—"}</StatNum>
          <StatLabel>Top size · {topSize ? topSize[1] : 0} orders</StatLabel>
        </StatCard>
        <StatCard>
          <StatNum>
            {todayCount}
            <span>+</span>
          </StatNum>
          <StatLabel>Pre-ordered today</StatLabel>
        </StatCard>
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Search name, email, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FilterSelect
          value={sizeFilter}
          onChange={(e) => setSizeFilter(e.target.value)}
        >
          <option value="">All sizes</option>
          {SIZES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {PREORDER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace("_", " ")}
            </option>
          ))}
        </FilterSelect>
        <ToggleGroup>
          <ToggleBtn
            $active={view === "table"}
            onClick={() => setView("table")}
          >
            ≡ Table
          </ToggleBtn>
          <ToggleBtn
            $active={view === "cards"}
            onClick={() => setView("cards")}
          >
            ⊞ Cards
          </ToggleBtn>
        </ToggleGroup>
        <ExportBtn onClick={exportCSV}>↓ Export CSV</ExportBtn>
        <ResultCount>
          {filtered.length} result{filtered.length !== 1 ? "s" : ""}
        </ResultCount>
      </Toolbar>

      {loading ? (
        <EmptyState>Loading pre-orders…</EmptyState>
      ) : filtered.length === 0 ? (
        <EmptyState>No pre-orders yet.</EmptyState>
      ) : view === "table" ? (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>#</Th>
                <Th onClick={() => handleSort("name")}>
                  Name{sortArrow("name")}
                </Th>
                <Th onClick={() => handleSort("email")}>
                  Email{sortArrow("email")}
                </Th>
                <Th>Phone</Th>
                <Th onClick={() => handleSort("size")}>
                  Size{sortArrow("size")}
                </Th>
                <Th onClick={() => handleSort("status")}>
                  Status{sortArrow("status")}
                </Th>
                <Th onClick={() => handleSort("createdAt")}>
                  Date{sortArrow("createdAt")}
                </Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o, i) => (
                <Tr key={o.id}>
                  <Td>
                    <PosBadge>{i + 1}</PosBadge>
                  </Td>
                  <Td>{o.name}</Td>
                  <Td $dim>{o.email}</Td>
                  <Td $dim>{o.phone || "—"}</Td>
                  <Td>
                    <SizeBadge>{o.size}</SizeBadge>
                  </Td>
                  <Td>
                    <StatusSelect
                      value={o.status}
                      onChange={(e) => handleStatusChange(o.id, e.target.value)}
                    >
                      {PREORDER_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s.replace("_", " ")}
                        </option>
                      ))}
                    </StatusSelect>
                  </Td>
                  <Td $dim>{formatDate(o.createdAt)}</Td>
                  <Td>
                    <DeleteBtn
                      onClick={() => handleDelete(o.id)}
                      title="Remove"
                    >
                      ✕
                    </DeleteBtn>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      ) : (
        <CardsGrid>
          {filtered.map((o, i) => (
            <UserCard key={o.id}>
              <CardTop>
                <CardAvatar>{initials(o.name)}</CardAvatar>
                <SizeBadge>{o.size}</SizeBadge>
              </CardTop>
              <CardName>{o.name}</CardName>
              <CardMeta>{o.email}</CardMeta>
              {o.phone && <CardMeta>{o.phone}</CardMeta>}
              <CardState>
                <StatusBadge $status={o.status}>
                  {o.status.replace("_", " ")}
                </StatusBadge>
              </CardState>
              <div
                style={{
                  marginTop: "0.75rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <CardMeta>{formatDate(o.createdAt)}</CardMeta>
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    alignItems: "center",
                  }}
                >
                  <StatusSelect
                    value={o.status}
                    onChange={(e) => handleStatusChange(o.id, e.target.value)}
                  >
                    {PREORDER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s.replace("_", " ")}
                      </option>
                    ))}
                  </StatusSelect>
                  <DeleteBtn onClick={() => handleDelete(o.id)} title="Remove">
                    ✕
                  </DeleteBtn>
                </div>
              </div>
            </UserCard>
          ))}
        </CardsGrid>
      )}
    </>
  );
}

// ── Main AdminPage ─────────────────────────────────────────────────────────────

export default function AdminPage() {
  const [user, setUser] = useState(undefined);
  const [activeTab, setActiveTab] = useState("waitlist");
  const [waitlistCount, setWaitlistCount] = useState(null);
  const [preorderCount, setPreorderCount] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return unsub;
  }, []);

  // Fetch counts for tab badges once authed
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [wSnap, pSnap] = await Promise.all([
          getDocs(
            query(collection(db, "waitlist"), orderBy("createdAt", "desc")),
          ),
          getDocs(
            query(collection(db, "preorders"), orderBy("createdAt", "desc")),
          ),
        ]);
        setWaitlistCount(wSnap.size);
        setPreorderCount(pSnap.size);
      } catch {}
    })();
  }, [user]);

  if (user === undefined)
    return <div style={{ minHeight: "100vh", background: "#040008" }} />;
  if (!user)
    return (
      <>
        <GlobalStyle />
        <Login />
      </>
    );

  return (
    <>
      <GlobalStyle />
      <AdminLayout>
        <AdminNav>
          <NavBrand>
            <NavLogo>NudR</NavLogo>
            <NavBadge>Admin</NavBadge>
          </NavBrand>
          <NavRight>
            <NavEmail>{user.email}</NavEmail>
            <SignOutBtn onClick={() => signOut(auth)}>Sign out</SignOutBtn>
          </NavRight>
        </AdminNav>

        <TabBar>
          <TabBtn
            $active={activeTab === "waitlist"}
            onClick={() => setActiveTab("waitlist")}
          >
            Waitlist
            {waitlistCount !== null && (
              <TabCount $active={activeTab === "waitlist"}>
                {waitlistCount}
              </TabCount>
            )}
          </TabBtn>
          <TabBtn
            $active={activeTab === "preorders"}
            onClick={() => setActiveTab("preorders")}
          >
            Pre-Orders
            {preorderCount !== null && (
              <TabCount $active={activeTab === "preorders"}>
                {preorderCount}
              </TabCount>
            )}
          </TabBtn>
        </TabBar>

        <Dashboard>
          {activeTab === "waitlist" ? <WaitlistTab /> : <PreordersTab />}
        </Dashboard>
      </AdminLayout>
    </>
  );
}
